import { useRef, useState } from "react";
import { motion } from "framer-motion";
import { Play, Pause, Check } from "lucide-react";
import { Equalizer } from "@/components/Equalizer";
import { siteAudioUrl } from "@/lib/api";

const fmt = (s) => {
  if (!isFinite(s)) return "0:00";
  const m = Math.floor(s / 60);
  return `${m}:${String(Math.floor(s % 60)).padStart(2, "0")}`;
};

export const PlaylistRow = ({ siteKey, take, songTitle, selected, added, anySelected, paid, onSelect, onToggleAdd, onTimeUpdate, bust }) => {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [time, setTime] = useState(0);
  const [duration, setDuration] = useState(0);

  const toggle = () => {
    const el = audioRef.current;
    if (!el) return;
    if (playing) {
      el.pause();
    } else {
      document.querySelectorAll("audio").forEach((a) => a !== el && a.pause());
      el.play();
    }
  };

  const seek = (e) => {
    const el = audioRef.current;
    if (!el || !duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    el.currentTime = ((e.clientX - rect.left) / rect.width) * duration;
  };

  return (
    <div
      data-testid={`playlist-row-${take.take_number}`}
      className={`relative overflow-hidden glass-panel rounded-2xl px-4 sm:px-5 py-4 transition-all duration-300 ${
        selected ? "border-gold/70 shadow-[0_0_36px_rgba(255,183,3,0.14)]" : "hover:border-white/25"
      }`}
    >
      {selected && (
        <motion.div
          data-testid={`pick-flash-${take.take_number}`}
          initial={{ opacity: 0.55 }}
          animate={{ opacity: 0 }}
          transition={{ duration: 1.4, ease: "easeOut" }}
          className="absolute inset-0 rounded-2xl bg-gold/25 pointer-events-none"
        />
      )}
      <audio
        ref={audioRef}
        src={siteAudioUrl(siteKey, take.batch_index, take.take_index, bust)}
        preload="metadata"
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onEnded={() => setPlaying(false)}
        onLoadedMetadata={(e) => setDuration(e.target.duration)}
        onTimeUpdate={(e) => {
          setTime(e.target.currentTime);
          if (selected && onTimeUpdate) onTimeUpdate(e.target.currentTime);
        }}
      />
      <div className="flex flex-wrap items-center gap-x-3 gap-y-3 sm:flex-nowrap sm:gap-4">
        <button
          data-testid={`take-${take.take_number}-play-btn`}
          onClick={toggle}
          className="order-1 h-11 w-11 shrink-0 rounded-full bg-gradient-to-br from-gold to-[#FB8500] flex items-center justify-center shadow-[0_0_20px_rgba(255,183,3,0.35)] hover:scale-105 active:scale-95 transition-transform cursor-pointer"
          aria-label={playing ? `Pause ${take.name}` : `Play ${take.name}`}
        >
          {playing ? <Pause className="h-4.5 w-4.5 h-5 w-5 text-black" /> : <Play className="h-5 w-5 text-black ml-0.5" />}
        </button>
        <div className="order-2 flex-1 min-w-0 basis-0">
          <div className="flex items-center gap-2">
            <p className="font-display font-bold text-sm sm:text-base leading-tight shrink-0">{take.name}</p>
            <span
              title={take.style}
              className="font-mono text-[10px] uppercase tracking-[0.15em] text-neon border border-neon/30 rounded-full px-2 py-0.5 whitespace-nowrap truncate min-w-0"
            >
              {take.style}
            </span>
          </div>
          <p className="text-xs text-slate-500 mt-0.5 leading-tight truncate">{songTitle}</p>
          <div className="flex items-center gap-3 mt-2.5">
            <div
              data-testid={`take-${take.take_number}-progress-bar`}
              onClick={seek}
              className="flex-1 h-1.5 rounded-full bg-white/10 cursor-pointer group"
            >
              <div
                className="h-full rounded-full bg-gradient-to-r from-gold to-coral transition-[width] duration-100"
                style={{ width: `${duration ? (time / duration) * 100 : 0}%` }}
              />
            </div>
            <span className="font-mono text-[10px] text-slate-500 shrink-0">{fmt(time)} / {fmt(duration)}</span>
            <Equalizer playing={playing} color="#00F0FF" className="h-4 hidden sm:flex" />
          </div>
        </div>
        {!paid && (
          <div className="order-3 w-full sm:w-auto shrink-0 flex items-center justify-end gap-3 sm:flex-col sm:items-end sm:gap-1.5">
            {selected ? (
              <span
                data-testid={`version-select-btn-${take.take_number}`}
                className="flex items-center gap-1.5 rounded-xl px-3.5 sm:px-4 py-2.5 text-sm font-bold bg-gold text-black"
              >
                <Check className="h-4 w-4" /> Picked
              </span>
            ) : !anySelected ? (
              <button
                data-testid={`version-select-btn-${take.take_number}`}
                onClick={() => onSelect(take)}
                className="flex items-center gap-1.5 rounded-xl px-3.5 sm:px-4 py-2.5 text-sm font-bold border border-white/20 text-slate-300 hover:border-gold hover:text-gold transition-all cursor-pointer"
              >
                Pick this take
              </button>
            ) : (
              <>
                <button
                  data-testid={`pick-instead-btn-${take.take_number}`}
                  onClick={() => onSelect(take)}
                  className="order-1 sm:order-2 text-xs font-semibold text-gold hover:text-neon underline underline-offset-4 transition-colors cursor-pointer"
                >
                  Pick this one instead
                </button>
                <button
                  data-testid={`version-select-btn-${take.take_number}`}
                  onClick={() => onToggleAdd(take)}
                  className={`order-2 sm:order-1 flex items-center gap-1.5 rounded-xl px-3.5 sm:px-4 py-2.5 text-sm font-bold transition-all cursor-pointer ${
                    added
                      ? "bg-neon/15 border border-neon text-neon"
                      : "border border-white/20 text-slate-300 hover:border-neon hover:text-neon"
                  }`}
                >
                  {added && <Check className="h-4 w-4" />}
                  {added ? "Added — $17" : "Add for $17"}
                </button>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
